import repeat from 'lodash/repeat'
import truncate from 'lodash/truncate'
import quotationDeadline from 'Enums/quotationDeadline'
import { userAvatar } from 'Services/avatarGenerator'
import { default as locale } from 'date-fns/locale/pt-BR'
import differenceInHours from 'date-fns/differenceInHours'
import formatDistanceStrict from 'date-fns/formatDistanceStrict'
import format from 'date-fns/format'
import parseISO from 'date-fns/parseISO'
import { QuoteOpportunityExpired, ApiQuoteOpportunityExpired } from 'Types'
import quotationType from 'Enums/quotationType'

/**
 * @param {ApiQuoteOpportunityPhone[]} data
 * @returns {string}
 */
const parsePhone = (data) => {
  const reducePhone = (acc, phone) => acc = {
    phone: `(${phone.ddd}) ${phone.number}`,
    isVerified: phone.isVerified
  }


  return data.slice(0).reduce(reducePhone, null)
}

/**
 * Returns a fake address with its length based on the city name
 * @param {string} address
 * @param {string} city
 * @returns {string}
 */
const parseStreet = (address, city) => {
  const regex = /\*(.+)/

  const repeatLength = city.length > 15
    ? Math.round(city.length / 2)
    : Math.round(city.length)

  const fixedAddress = address.split(regex).join(' ').trim()
  const fakeAsterisk = repeat('*', repeatLength)

  return `${fixedAddress} ${fakeAsterisk}`
}

/**
 * @param {ApiQuoteOpportunityForm} data
 * @returns {QuoteOpportunityForm}
 */
const parseForm = (formItem) => {
  const { question, answer, inputTypeFormat, isBuildingSpecification } = formItem
  const type = inputTypeFormat.toUpperCase()


  return {
    question,
    answer,
    type,
    isBuildingSpecification: isBuildingSpecification || false // TODO Remove false when API is complete
  }
}

/**
 * Returns a fake address name
 * @param {string} addressName
 */
const parseAdressName = (addressName) => {
  const adressWordList = addressName.split(/\s/)

  const [firstWord] = adressWordList

  const otherWords = adressWordList.filter(w => w !== firstWord).map(word => {
    const halfLength = word.length / 2

    return word.slice(0, halfLength) + repeat('*', halfLength)
  })

  return [firstWord, ...otherWords].join(' ')
}

/**
 * @param {ApiQuoteOpportunityPreview} data
 * @returns {QuoteOpportunityPreview}
 */
export const parseOpportunityPreview = (data) => {
  const address = {
    name: parseAdressName(data.addressName),
    neighborhood: data.address.neighborhood,
    state: data.address.state,
    city: data.address.city,
  }

  const dateCreated = parseISO(data.dateCreated)
  const dateExpiration = parseISO(data.expirationDate)

  const date = {
    creation: format(dateCreated, `dd/MM \'às\' HH:MM`, { locale }),
    expiration: format(dateExpiration, 'dd/MM \'às\' HH:MM', { locale }),
    createdSince: formatDistanceStrict(dateCreated, new Date(), { locale, roundingMethod: 'ceil' }),
    expiresIn: formatDistanceStrict(dateExpiration, new Date(), { locale, roundingMethod: 'ceil' }),
    distanceInHours: differenceInHours(dateExpiration, new Date()),
  }

  const sender = {
    id: data.userId,
    name: data.senderName,
    avatar: userAvatar(data.senderPhoto, data.senderName),
    isNew: data.isNewUser,
    frequency: null,
    occupation: data.occupation,
    rate: data.rating
  }

  const negotiationId = typeof data.companyQuotationId !== 'undefined'
    ? data.companyQuotationId
    : null

  return {
    id: data.quotationId,
    negotiationId: negotiationId,
    token: data.quotationIdentifier,
    category: data.category,
    amount: data.amount,
    serviceType: data.serviceType,
    type: data.type,
    quotationType: data.quotationType,
    origin: data.origin,
    stock: data.stock,
    description: truncate(data.description, { length: 145 }),
    hasFiles: data.hasFiles,
    address,
    date,
    sender,
  }
}

/** @param {ApiQuoteOpportunityDetail} item */
export const parseOpportunityDetail = (item) => {
  const negotiationId = (typeof item.companyQuotationId !== 'undefined')
    ? item.companyQuotationId
    : null

  const dateCreated = parseISO(item.dateCreated)
  const dateExpiration = parseISO(item.expirationDate)
  const questionList = item.answers.map(parseForm)


  return {
    id: item.id,
    negotiationId: negotiationId,
    token: item.identifier,
    category: item.category,
    serviceType: item.serviceType,
    isBrandNew: false,
    isPublic: negotiationId === null,
    isAvailable: item.available,
    reasonForFailure: item.reason,
    quotationType: item.quotationType,
    origin: item.originCompanyQuotation,
    companyQuotationId: item.companyQuotationId,
    price: {
      previous: item.amountOriginal,
      current: item.amount,
      discount: (100 - (item.amount / item.amountOriginal * 100)).toFixed(2),
      currency: item.amountFormat
    },
    address: {
      description: item.address.description,
      street: parseStreet(item.address.street, item.address.city),
      neighborhood: item.address.neighborhood,
      city: item.address.city,
      state: item.address.state,
    },
    sender: {
      id: item.sender.id,
      name: item.sender.name,
      email: item.sender.email,
      avatar: userAvatar(item.sender.photo, item.sender.name),
      phoneInfo: parsePhone(item.phones),
      rate: item.sender.rate,
      contactPreferenceDescription: item.sender.contactPreferenceDescription,
      contactPreference: item.sender.contactPreference
    },
    request: {
      questionFormList: questionList.filter(answer => answer.isBuildingSpecification === false),
      questionBuildingList: questionList.filter(answer => answer.isBuildingSpecification === true),
      details: item.description,
      deadline: quotationDeadline.getDescriptionByValue(item.periodStartOfService),
      attachments: item.filesCount,
    },
    date: {
      creation: format(dateCreated, 'dd/MM/yyyy \'às\' HH:MM', { locale }),
      expiration: format(dateExpiration, 'dd/MM/yyyy \'às\' HH:MM', { locale }),
      createdSince: formatDistanceStrict(dateCreated, new Date(), { locale, roundingMethod: 'ceil' }),
      expiresIn: formatDistanceStrict(dateExpiration, new Date(), { locale, roundingMethod: 'ceil' }),
      distanceInHours: differenceInHours(dateExpiration, new Date()),
    }
  }
}

export const parsePublicOpportunityDetail = (data) => {
  const isPublic = true

  const publicOpportunityDetail = Object.assign(
    parseOpportunityDetail(data), { isPublic }
  )

  return publicOpportunityDetail
}

export const parseExpiredQuotation = (data: ApiQuoteOpportunityExpired): QuoteOpportunityPreview => ({
  token: data.quotationIdentifier,
  serviceType: data.serviceType,
  created: format(parseISO(data.dateCreated), `dd \'de\' MMM \'às\' HH:MM`, { locale }),
  description: data.description,
  type: data.selectedByRequester ? quotationType.special : quotationType.smart,
  address: {
    name: parseAdressName(data.addressName),
    neighborhood: data.neighborhood,
    state: data.state,
    city: data.city,
  },
  sender: {
    name: data.senderName,
    avatar: userAvatar(data.senderPhoto, data.senderName),
    isNew: false,
    occupation: '',
  }
})

export const parseDiscardReason = (discardReasonId, negotiationId, discardDescription): ApiQuoteOpportunityDiscard => {
  return {
    discardReason: discardReasonId,
    discardDescription: discardDescription,
    companyQuotationId: negotiationId,
  }
}

export default {
  parseOpportunityPreview,
  parseOpportunityDetail,
  parsePublicOpportunityDetail,
  parseExpiredQuotation,
}